  import React from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MdFacebook, MdEmail } from 'react-icons/md';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import type { ScheduledCampaign } from './types';
import { formatUTCDateTime } from '../../utils/dateUtils';
import { openChannelPost } from '../../utils/channelUtils';

interface CampaignDetailModalProps {
  campaign: ScheduledCampaign | null;
  isOpen: boolean;
  onClose: () => void;
  clickPosition?: { x: number; y: number } | null;
}

const CampaignDetailModal: React.FC<CampaignDetailModalProps> = ({ campaign, isOpen, onClose, clickPosition }) => {
  const dispatch = useAppDispatch();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  
  // Local state for loading indicators
  const [updating, setUpdating] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  
  const [modalPosition, setModalPosition] = React.useState<{ top?: string; left?: string; right?: string; bottom?: string }>({});
  const modalRef = React.useRef<HTMLDivElement>(null);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [editedStartDate, setEditedStartDate] = React.useState('');
  const [editedEndDate, setEditedEndDate] = React.useState('');
  const [editedScheduleDate, setEditedScheduleDate] = React.useState('');
  const [editedScheduleTime, setEditedScheduleTime] = React.useState('');
  const [selectedPlatforms, setSelectedPlatforms] = React.useState<string[]>([]);
  const [availablePlatforms] = React.useState<string[]>(['FACEBOOK', 'INSTAGRAM', 'TIKTOK', 'EMAIL']);

  React.useEffect(() => {
    if (isOpen && campaign) {
      setIsEditMode(false);
      const startDate = (campaign as any).startDate || (campaign as any).start_date;
      const endDate = (campaign as any).endDate || (campaign as any).end_date;
      
      if (startDate) {
        const dateObj = new Date(startDate);
        setEditedStartDate(formatDateForInput(dateObj));
        setEditedScheduleDate(formatDateForInput(dateObj));
        // Extract time in HH:MM format
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        setEditedScheduleTime(`${hours}:${minutes}`);
      }
      if (endDate) {
        setEditedEndDate(formatDateForInput(new Date(endDate)));
      }
      setSelectedPlatforms(getPlatforms());
    }
  }, [isOpen, campaign]);

  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate modal position based on click position
  React.useEffect(() => {
    if (!isOpen || !clickPosition) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const modalWidth = 320;
    // Use estimated modal height (image + content ~400px)
    const estimatedModalHeight = 400;
    const spacing = 16;

    let position: { top?: string; left?: string; right?: string; bottom?: string } = {};

    // Calculate horizontal position
    const spaceOnRight = viewportWidth - clickPosition.x;
    const spaceOnLeft = clickPosition.x;

    if (spaceOnRight >= modalWidth + spacing) {
      position.left = `${clickPosition.x + spacing}px`;
    } else if (spaceOnLeft >= modalWidth + spacing) {
      position.right = `${viewportWidth - clickPosition.x + spacing}px`;
    } else {
      position.left = '50%';
    }

    // Calculate vertical position
    const spaceBelow = viewportHeight - clickPosition.y;
    const spaceAbove = clickPosition.y;

    // If not enough space below, position above the click
    if (spaceBelow < estimatedModalHeight + spacing && spaceAbove >= spacing) {
      // Position above the click point
      position.bottom = `${viewportHeight - clickPosition.y + spacing}px`;
    } else if (spaceBelow >= spacing) {
      // Position below the click point
      position.top = `${clickPosition.y + spacing}px`;
    } else {
      // Fallback to center
      position.top = '50%';
    }

    setModalPosition(position);
  }, [isOpen, clickPosition]);

  // Close modal on Escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Close edit mode when save succeeds
  const prevUpdatingRef = React.useRef(false);
  React.useEffect(() => {
    if (prevUpdatingRef.current && !updating) {
      // Save completed successfully
      setIsEditMode(false);
    }
    prevUpdatingRef.current = updating;
  }, [updating]);

  // Close modal when delete succeeds
  const prevDeletingRef = React.useRef(false);
  React.useEffect(() => {
    if (prevDeletingRef.current && !deleting) {
      // Delete completed successfully - close modal
      onClose();
    }
    prevDeletingRef.current = deleting;
  }, [deleting, onClose]);

  if (!campaign) return null;

  const getFlyerUrl = (): string | null => {
    // NEW API structure: campaign.flyer.image.storage_url
    if ((campaign as any).flyer?.image?.storage_url) {
      return (campaign as any).flyer.image.storage_url;
    }
    // Check for nested image structure
    if ((campaign as any).flyer?.image?.image?.storage_url) {
      return (campaign as any).flyer.image.image.storage_url;
    }
    // Fallback to old structures
    if ((campaign as any).campaign?.flyerUrl) {
      return (campaign as any).campaign.flyerUrl;
    }
    const platformPosts = (campaign as any).platformPosts || (campaign as any).platform_posts;
    if (platformPosts && platformPosts.length > 0) {
      return platformPosts[0].url;
    }
    if (campaign.flyer_info?.flyer_url) {
      return campaign.flyer_info.flyer_url;
    }
    return null;
  };


  const getValidDates = () => {
    // New API structure: campaign.campaign.validFrom and validTo
    if ((campaign as any).campaign?.validFrom) {
      return {
        validFrom: (campaign as any).campaign.validFrom,
        validTo: (campaign as any).campaign.validTo
      };
    }
    return { validFrom: null, validTo: null };
  };

  const shouldBlink = (): boolean => {
    const { validTo } = getValidDates();
    if (!validTo) return false;
    
    const now = new Date();
    const validToDate = new Date(validTo);
    return now < validToDate; // Blink only if current date is before validTo
  };

  const getPlatforms = (): string[] => {
    if ((campaign as any).platforms && Array.isArray((campaign as any).platforms)) {
      return (campaign as any).platforms;
    }
    // Old API structure
    if (campaign.social_media) {
      return parseSocialMedia(campaign.social_media);
    }
    return [];
  };

  const parseSocialMedia = (socialMedia: string): string[] => {
    try {
      if (socialMedia.startsWith('[')) {
        const parsed = JSON.parse(socialMedia);
        return Array.isArray(parsed) ? parsed : [socialMedia];
      }
      return socialMedia.split(',').map(p => p.trim());
    } catch {
      return socialMedia.split(',').map(p => p.trim());
    }
  };

  const getPlatformIcon = (platform: string) => {
    const upperPlatform = platform.toUpperCase();
    switch (upperPlatform) {
      case 'FACEBOOK':
        return <MdFacebook className="text-blue-500" size={20} />;
      case 'INSTAGRAM':
        return <FaInstagram className="text-pink-500" size={20} />;
      case 'TIKTOK':
        return <FaTiktok className="text-gray-100" size={20} />;
      case 'EMAIL':
        return <MdEmail className="text-yellow-500" size={20} />;
      default:
        return null;
    }
  };


  const getPositionStyles = (): React.CSSProperties => {
    if (!clickPosition) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      };
    }

    const style: React.CSSProperties = { position: 'fixed' };

    if (modalPosition.top) {
      style.top = modalPosition.top;
      if (modalPosition.left === '50%') {
        style.transform = 'translateX(-50%)';
      }
    }
    if (modalPosition.bottom) {
      style.bottom = modalPosition.bottom;
      if (modalPosition.left === '50%') {
        style.transform = 'translateX(-50%)';
      }
    }
    if (modalPosition.left && modalPosition.left !== '50%') {
      style.left = modalPosition.left;
    } else if (modalPosition.left === '50%') {
      style.left = '50%';
    }
    if (modalPosition.right) {
      style.right = modalPosition.right;
    }

    if (modalPosition.top === '50%' && modalPosition.left === '50%') {
      style.transform = 'translate(-50%, -50%)';
    }

    return style;
  };

  const flyerUrl = getFlyerUrl();
  const platforms = getPlatforms();
  
  // Check if campaign is posted (has publishData with status "posted")
  const isPosted = (campaign as any).publishData && 
                   Array.isArray((campaign as any).publishData) && 
                   (campaign as any).publishData.length > 0 &&
                   (campaign as any).publishData.some((pub: any) => pub.status === 'posted');

  const togglePlatform = (platform: string) => {
    const upperPlatform = platform.toUpperCase();
    if (selectedPlatforms.some(p => p.toUpperCase() === upperPlatform)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p.toUpperCase() !== upperPlatform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, upperPlatform]);
    }
  };

  const unselectedPlatforms = availablePlatforms.filter(available => 
    !selectedPlatforms.some(selected => selected.toUpperCase() === available.toUpperCase())
  );

  const handleSave = () => {
    if (!restaurantId) {
      console.error('Restaurant ID not found');
      return;
    }

    const campaignId = (campaign as any).id || (campaign as any).campaignId;
    if (!campaignId) {
      console.error('Campaign ID not found');
      return;
    }

    // TODO: Implement campaign update API call when available
    
    // Close edit mode after a brief delay to simulate save
    setUpdating(true);
    setTimeout(() => {
      setUpdating(false);
      setIsEditMode(false);
    }, 500);
  };

  const handleDelete = () => {
    if (!restaurantId) {
      console.error('Restaurant ID not found');
      return;
    }

    const campaignId = (campaign as any).id || (campaign as any).campaignId;
    if (!campaignId) {
      console.error('Campaign ID not found');
      return;
    }

    // TODO: Implement campaign delete API call when available
    
    // Close modal after a brief delay to simulate delete
    setDeleting(true);
    setTimeout(() => {
      setDeleting(false);
      onClose();
    }, 500);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with minimal opacity - no blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/10 z-50"
          />

          {/* Modal with slide and scale animation */}
          <div className="fixed z-[60]" style={getPositionStyles()}>
            {/* Close button - outside modal */}
            <button
              onClick={onClose}
              className="absolute cursor-pointer top-[-8px] right-[-8px] w-7 h-7 flex items-center justify-center rounded-full transition-colors z-[100] bg-orange-600 hover:bg-orange-700 shadow-lg"
              aria-label="Close modal"
            >
              <svg className="w-4 h-4 text-white font-bold" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal container */}
            <motion.div
              ref={modalRef}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="rounded-md w-80 max-h-[85vh] overflow-hidden"
              style={{
                backdropFilter: 'bg-brand-primary',
                boxShadow: '0 0 14px 15pxrgba(255, 255, 255, 0.12)',
                background: '#212121',
                border: '3px solid #ee4423'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Content */}
              <div className="overflow-y-auto max-h-[85vh]">
              {/* Flyer Image with Overlay */}
              {flyerUrl && (
                <div className="relative">
                  <img 
                    src={flyerUrl} 
                    alt="Campaign Flyer" 
                    className="w-full h-auto object-cover object-top max-h-"
                    onError={(e) => {
                      e.currentTarget.src = 'https://via.placeholder.com/320x180/212121/EE4423?text=Flyer';
                    }}
                  />
                  
                  {/* Floating Edit Icon - Bottom Right - only for non-posted campaigns */}
                  {!isPosted && !isEditMode && (
                    <button
                      onClick={() => setIsEditMode(true)}
                      className="absolute bottom-3 cursor-pointer right-3 w-10 h-10 bg-orange-600 hover:bg-orange-700 rounded-full flex items-center justify-center shadow-lg transition-all z-10"
                      aria-label="Edit campaign"
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                  
                  {/* Posted Info - Show for posted campaigns */}
                  {isPosted && !isEditMode && (() => {
                    const publishEntries = (campaign as any)._publishEntries || 
                      (campaign as any).publishData?.filter((pub: any) => pub.status === 'posted') || [];
                    const postDate = publishEntries.length > 0 ? publishEntries[0].date : null;
                    
                    return (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            {publishEntries.map((pub: any, index: number) => (
                              <button
                                key={index}
                                onClick={() => openChannelPost({ 
                                  channel: pub.channel, 
                                  postId: pub.postId,
                                  shortcode: pub.shortcode
                                })}
                                className="p-1.5 bg-black/50 rounded-full cursor-pointer hover:opacity-80 transition-opacity"
                                title={`View post on ${pub.channel}`}
                              >
                                {getPlatformIcon(pub.channel)}
                              </button>
                            ))}
                          </div>
                          {/* Show date only once */}
                          {postDate && (
                            <span className="text-[10px] text-gray-300">
                              {formatUTCDateTime(postDate)}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Edit Overlay - Shows at bottom of flyer when in edit mode */}
                  {isEditMode && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      className="absolute bottom-0 left-0 right-0 text-brand-primary backdrop-blur-lg p-3 space-y-3"
                      style={{
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                      }}
                    >
                      {/* Delete, Cancel and Save buttons */}
                      <div className="flex justify-between items-center">
                        <button
                          onClick={handleDelete}
                          disabled={deleting || updating}
                          className="cursor-pointer p-1 rounded bg-brand-primary rounded-full hover:bg-red-600/20 transition-colors disabled:opacity-50"
                          title={deleting ? 'Deleting...' : 'Delete campaign'}
                        >
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setIsEditMode(false)}
                            disabled={updating || deleting}
                            className="text-xs font-normal text-white cursor-pointer transition-colors disabled:opacity-50">
                            Cancel
                          </button>
                          <button
                            onClick={handleSave}
                            disabled={updating || deleting}
                            className="text-xs font-normal px-3 py-1 rounded bg-brand-primary text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                            {updating ? 'Saving...' : deleting ? 'Deleting...' : 'Save'}
                          </button>
                        </div>
                      </div>

                {(() => {
                  const startDate = (campaign as any).startDate || (campaign as any).start_date;
                  const endDate = (campaign as any).endDate || (campaign as any).end_date;
                  const postedAt = (campaign as any).postedAt || (campaign as any).posted_at;
                  
                  // Show posted date for post_now campaigns
                  if (!startDate && !endDate && postedAt) {
                    return (
                      <div>
                        <label className="block text-[9px] font-medium mb-1 text-orange-300">Posted On</label>
                        <div className="text-[10px] px-2 py-1.5 rounded bg-gray-900/60 border border-orange-900/50 text-orange-200">
                          {new Date(postedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </div>
                      </div>
                    );
                  }
                  
                  // Show Schedule Date and Time for new scheduled campaigns (startDate only, no endDate)
                  if (startDate && !endDate) {
                    const dateObj = new Date(startDate);
                    return (
                      <div>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="block text-[9px] font-medium mb-1 text-white">Schedule Date</label>
                            {isEditMode ? (
                              <input
                                type="date"
                                value={editedScheduleDate}
                                onChange={(e) => setEditedScheduleDate(e.target.value)}
                                className="w-full text-[10px] px-2 py-1.5 rounded bg-gray-900/60 border border-orange-900/50 text-orange-200 focus:outline-none focus:border-orange-500"
                              />
                            ) : (
                              <div className="text-[10px] px-2 py-1.5 rounded bg-gray-900/60 border border-orange-900/50 text-orange-200">
                                {dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <label className="block text-[9px] font-medium mb-1 text-white">Time</label>
                            {isEditMode ? (
                              <input
                                type="time"
                                value={editedScheduleTime}
                                onChange={(e) => setEditedScheduleTime(e.target.value)}
                                className="w-full text-[10px] px-2 py-1.5 rounded bg-gray-900/60 border border-orange-900/50 text-orange-200 focus:outline-none focus:border-orange-500"
                              />
                            ) : (
                              <div className="text-[10px] px-2 py-1.5 rounded bg-gray-900/60 border border-orange-900/50 text-orange-200">
                                {dateObj.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                              </div>
                            )}
                          </div>

                        </div>
                      </div>
                    );
                  }
                  
                  // Show duration for old scheduled campaigns (both startDate and endDate)
                  if (startDate && endDate) {
                    return (
                      <div>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="block text-[9px] font-medium mb-1 text-orange-300">From</label>
                            {isEditMode ? (
                              <input
                                type="date"
                                value={editedStartDate}
                                onChange={(e) => setEditedStartDate(e.target.value)}
                                className="w-full text-[10px] px-2 py-1.5 rounded bg-gray-900/60 border border-orange-900/50 text-orange-200 focus:outline-none focus:border-orange-500"
                              />
                            ) : (
                              <div className="text-[10px] px-2 py-1.5 rounded bg-gray-900/60 border border-orange-900/50 text-orange-200">
                                {new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <label className="block text-[9px] font-medium mb-1 text-orange-300">To</label>
                            {isEditMode ? (
                              <input
                                type="date"
                                value={editedEndDate}
                                onChange={(e) => setEditedEndDate(e.target.value)}
                                className="w-full text-[10px] px-2 py-1.5 rounded bg-gray-900/60 border border-orange-900/50 text-orange-200 focus:outline-none focus:border-orange-500"
                              />
                            ) : (
                              <div className="text-[10px] px-2 py-1.5 rounded bg-gray-900/60 border border-orange-900/50 text-orange-200">
                                {new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  return null;
                })()}

                      {/* Platforms */}
                      <div>
                        <label className="block text-[9px] font-medium mb-2 text-white">Platforms</label>
                        
                        <div className="space-y-2">
                          {/* Selected Platforms */}
                          {selectedPlatforms.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {selectedPlatforms.map((platform, index) => (
                                <div
                                  key={index}
                                  className="relative flex items-center gap-1 p-2 rounded-full bg-gray-800 border border-white/30 group"
                                >
                                  {getPlatformIcon(platform)}
                                  <button
                                    onClick={() => togglePlatform(platform)}
                                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-brand-primary cursor-pointer flex items-center justify-center text-white text-[10px] font-bold"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Unselected Platforms */}
                          {unselectedPlatforms.length > 0 && (
                            <div>
                              <p className="text-[9px] text-white/70 mb-1">Add more platforms</p>
                              <div className="flex flex-wrap gap-2">
                                {unselectedPlatforms.map((platform, index) => (
                                  <button
                                    key={index}
                                    onClick={() => togglePlatform(platform)}
                                    className="flex items-center gap-1 p-2 rounded-full cursor-pointer bg-gray-800/50 border border-white/20 hover:border-white/50 transition-colors"
                                  >
                                    {getPlatformIcon(platform)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CampaignDetailModal;
